import React, { useMemo, useCallback, memo } from 'react';
import moment from 'moment';
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import companyIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Company_Name.svg';
import designationIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Role.svg';
import locationIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Location.svg';
import jobIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Job_Brief_Icon.svg';
import fromDateIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/From_Date.svg';
import toDateIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/To_Date.svg';
import actionIcon from '../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Action.svg';

const PreviousEmpComponent = memo(({
  data = [],
  canEdit = false,
  onEdit,
  emptyMessage = "No Previous Employment Details Available",
}) => {
  const columns = useMemo(() => [
    {
      key: 'CompanyName',
      label: 'Company Name',
      icon: companyIcon,
      width: '14%'
    },
    {
      key: 'Position',
      label: 'Designation',
      icon: designationIcon,
      width: '14%'
    },
    {
      key: 'Location',
      label: 'Location',
      icon: locationIcon,
      width: '12%'
    },
    {
      key: 'JobDescription',
      label: 'Job Brief',
      icon: jobIcon,
      width: '20%'
    },
    {
      key: 'FromDate',
      label: 'From Date',
      icon: fromDateIcon,
      isDate: true,
      center: true,
      width: '10%'
    },
    {
      key: 'ToDate',
      label: 'To Date',
      icon: toDateIcon,
      isDate: true,
      center: true,
      width: '10%'
    },
    {
      key: 'OrganizationAddress',
      label: 'Address',
      icon: locationIcon,
      width: '14%'
    }
  ], []);

  const formatDate = useCallback((date) => {
    return date ? moment(date).format('DD-MMM-YYYY') : '';
  }, []);

  const renderTooltip = useCallback((text) => <Tooltip>{text}</Tooltip>, []);

  const renderCellContent = useCallback((text, isDate = false, center = false) => {
    const displayText = isDate ? formatDate(text) : text || '';
    
    return (
      <OverlayTrigger
        placement="top"
        overlay={renderTooltip(displayText)}
      >
        <div 
          className={`text-truncate ${center ? 'text-center' : ''}`} 
          style={{ 
            width: '100%',
            display: 'inline-block'
          }}
        >
          {displayText}
        </div>
      </OverlayTrigger>
    );
  }, [formatDate, renderTooltip]);

  const handleEdit = useCallback((item) => {
    if (canEdit && onEdit) {
      onEdit(item);
    }
  }, [canEdit, onEdit]);

  return (
    <div className="previous-emp-table-container">
      <style>
        {`
          .previous-emp-table-container {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .fixed-table {
            table-layout: fixed;
            width: 100%;
            min-width: 800px; /* Minimum width to ensure columns don't collapse */
          }
          
          .fixed-width-column {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @media (min-width: 992px) {
            .fixed-table {
              min-width: 100%;
            }
          }
        `}
      </style>
      
      <Table striped bordered hover className="fixed-table">
        <thead className="text-center">
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={{ width: column.width }}>
                <div className="d-flex flex-column align-items-center fixed-width-column">
                  <div>
                    <img
                      src={column.icon}
                      alt={column.label}
                      className="tech-icon"
                      style={{ width: '20px', height: '20px' }}
                    />
                  </div>
                  <div style={{ fontSize: "12px" }}>{column.label}</div>
                </div>
              </th>
            ))}
            <th style={{ width: '6%' }}>
              <div className="d-flex flex-column align-items-center fixed-width-column">
                <div>
                  <img
                    src={actionIcon}
                    alt="Action"
                    className="tech-icon"
                    style={{ width: '20px', height: '20px' }}
                  />
                </div>
                <div style={{ fontSize: "12px" }}>Action</div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td 
                    key={column.key} 
                    className={`fixed-width-column ${column.center ? "text-center" : ""}`}
                    style={{ width: column.width }}
                  >
                    {renderCellContent(
                      item[column.key],
                      column.isDate,
                      column.center
                    )}
                  </td>
                ))}
                <td className="text-center fixed-width-column" style={{ width: '6%' }}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{canEdit ? "Edit" : "Edit disabled"}</Tooltip>}
                  >
                    <span
                      onClick={() => handleEdit(item)}
                      style={{
                        cursor: canEdit ? "pointer" : "not-allowed",
                        opacity: canEdit ? 1 : 0.5,
                      }}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </span>
                  </OverlayTrigger>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length + 1} className="text-center">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
});

export default PreviousEmpComponent;